import React, { useState, useEffect } from 'react';
import './TransactionList.css';
import apiClient from '../../api';
import { formatDateTime, formatCurrency } from '../../utils/dateUtils';

const TransactionList = () => {
    const [transactions, setTransactions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [expandedUsers, setExpandedUsers] = useState({});

    useEffect(() => {
        fetchTransactions();
    }, []);

    const fetchTransactions = async () => {
        try {
            setLoading(true);
            const response = await apiClient.get('/api/payments/transactions/admin/');
            const data = response.data;

            if (data.success && Array.isArray(data.transactions)) {
                setTransactions(data.transactions);
            } else {
                setTransactions([]);
            }
            setError(null);
        } catch (error) {
            console.error('Error fetching transactions:', error);
            setError('Failed to load transactions');
        } finally {
            setLoading(false);
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleString();
    };

    const formatAmount = (amount) => {
        if (!amount && amount !== 0) return '₹0.00';
        return `₹${Number(amount).toFixed(2)}`;
    };

    const getStatusClass = (status) => {
        switch (status?.toLowerCase()) {
            case 'success':
                return 'status-success';
            case 'pending':
                return 'status-pending';
            case 'failed':
                return 'status-failed';
            default:
                return 'status-unknown';
        }
    };

    const handleCardFilter = (status) => {
        setStatusFilter(prev => prev === status ? 'all' : status);
    };

    const toggleUserExpand = (userKey) => {
        setExpandedUsers(prev => ({
            ...prev,
            [userKey]: !prev[userKey]
        }));
    };

    const filteredTransactions = transactions.filter(tx => {
        const matchesSearch =
            (tx.user_name && tx.user_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
            (tx.user_email && tx.user_email.toLowerCase().includes(searchTerm.toLowerCase())) ||
            (tx.purpose && tx.purpose.toLowerCase().includes(searchTerm.toLowerCase())) ||
            (tx.razorpay_payment_id && tx.razorpay_payment_id.toLowerCase().includes(searchTerm.toLowerCase()));

        const matchesStatus = statusFilter === 'all' || tx.status?.toLowerCase() === statusFilter;

        return matchesSearch && matchesStatus;
    });

    // Group transactions by user email (or user_name if email not available)
    const groupedTransactions = filteredTransactions.reduce((groups, tx) => {
        const userKey = tx.user_email || tx.user_name || 'unknown';
        if (!groups[userKey]) {
            groups[userKey] = {
                user_name: tx.user_name || 'Unknown',
                user_email: tx.user_email || 'No email',
                transactions: [],
                totalAmount: 0,
                successAmount: 0
            };
        }
        groups[userKey].transactions.push(tx);
        if (tx.status?.toLowerCase() === 'success') {
            groups[userKey].successAmount += Number(tx.amount) || 0;
        }
        groups[userKey].totalAmount += Number(tx.amount) || 0;
        return groups;
    }, {});

    // Calculate totals from search-only filtered data (so counts stay correct regardless of status filter)
    const searchFilteredTransactions = transactions.filter(tx => {
        if (!searchTerm) return true;
        return (
            (tx.user_name && tx.user_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
            (tx.user_email && tx.user_email.toLowerCase().includes(searchTerm.toLowerCase())) ||
            (tx.purpose && tx.purpose.toLowerCase().includes(searchTerm.toLowerCase())) ||
            (tx.razorpay_payment_id && tx.razorpay_payment_id.toLowerCase().includes(searchTerm.toLowerCase()))
        );
    });

    const totalAmount = searchFilteredTransactions
        .filter(tx => tx.status?.toLowerCase() === 'success')
        .reduce((sum, tx) => sum + (Number(tx.amount) || 0), 0);

    const successCount = searchFilteredTransactions.filter(tx => tx.status?.toLowerCase() === 'success').length;
    const pendingCount = searchFilteredTransactions.filter(tx => tx.status?.toLowerCase() === 'pending').length;
    const failedCount = searchFilteredTransactions.filter(tx => tx.status?.toLowerCase() === 'failed').length;

    if (loading) {
        return <div className="loading-message">Loading transactions...</div>;
    }

    if (error) {
        return <div className="error-message">{error}</div>;
    }

    return (
        <div className="transaction-list">
            <div className="transaction-header">
                <h1 className="transaction-title">💳 Transaction History</h1>
            </div>

            {/* Stats Cards */}
            <div className="transaction-stats">
                <div
                    className={`stat-card stat-total clickable-card ${statusFilter === 'all' ? 'active-card' : ''}`}
                    onClick={() => setStatusFilter('all')}
                    title="Click to show all transactions"
                >
                    <div className="stat-icon">💰</div>
                    <div className="stat-content">
                        <div className="stat-value">{formatAmount(totalAmount)}</div>
                        <div className="stat-label">Total Revenue</div>
                    </div>
                </div>
                <div
                    className={`stat-card stat-success clickable-card ${statusFilter === 'success' ? 'active-card' : ''}`}
                    onClick={() => handleCardFilter('success')}
                    title="Click to filter Successful transactions"
                >
                    <div className="stat-icon">✅</div>
                    <div className="stat-content">
                        <div className="stat-value">{successCount}</div>
                        <div className="stat-label">Successful</div>
                    </div>
                </div>
                <div
                    className={`stat-card stat-pending-card clickable-card ${statusFilter === 'pending' ? 'active-card' : ''}`}
                    onClick={() => handleCardFilter('pending')}
                    title="Click to filter Pending transactions"
                >
                    <div className="stat-icon">⏳</div>
                    <div className="stat-content">
                        <div className="stat-value">{pendingCount}</div>
                        <div className="stat-label">Pending</div>
                    </div>
                </div>
                <div
                    className={`stat-card stat-failed-card clickable-card ${statusFilter === 'failed' ? 'active-card' : ''}`}
                    onClick={() => handleCardFilter('failed')}
                    title="Click to filter Failed transactions"
                >
                    <div className="stat-icon">❌</div>
                    <div className="stat-content">
                        <div className="stat-value">{failedCount}</div>
                        <div className="stat-label">Failed</div>
                    </div>
                </div>
            </div>

            {/* Filters */}
            <div className="filters-container">
                <input
                    type="text"
                    placeholder="🔍 Search by name, email, purpose, or payment ID..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="search-input"
                />
                <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="status-filter"
                >
                    <option value="all">All Status</option>
                    <option value="success">Success</option>
                    <option value="pending">Pending</option>
                    <option value="failed">Failed</option>
                </select>
            </div>

            {filteredTransactions.length === 0 && transactions.length === 0 ? (
                <div className="empty-state">
                    <div className="empty-icon">💳</div>
                    <h3 className="empty-title">No Transactions Found</h3>
                    <p className="empty-description">
                        No payment transactions have been made yet. Transactions will appear here once users make payments.
                    </p>
                </div>
            ) : filteredTransactions.length === 0 ? (
                <div className="empty-state">
                    <div className="empty-icon">🔍</div>
                    <h3 className="empty-title">No Matching Transactions</h3>
                    <p className="empty-description">
                        Try adjusting your search or filter to find transactions.
                    </p>
                </div>
            ) : (
                <div className="user-groups-container">
                    {Object.entries(groupedTransactions).map(([userKey, userData]) => {
                        const isExpanded = expandedUsers[userKey];
                        const hasMultipleTransactions = userData.transactions.length > 1;

                        return (
                            <div key={userKey} className="user-group">
                                <div
                                    className={`user-group-header ${hasMultipleTransactions ? 'clickable' : ''}`}
                                    onClick={() => hasMultipleTransactions && toggleUserExpand(userKey)}
                                >
                                    <div className="user-group-info">
                                        <div className="user-avatar-large">
                                            {userData.user_name ? userData.user_name.substring(0, 2).toUpperCase() : '??'}
                                        </div>
                                        <div className="user-group-details">
                                            <div className="user-group-name">{userData.user_name}</div>
                                            <div className="user-group-email">{userData.user_email}</div>
                                        </div>
                                        <div className="transaction-count-badge">
                                            {userData.transactions.length} transaction{userData.transactions.length > 1 ? 's' : ''}
                                        </div>
                                        <div className="user-total-amount">
                                            {formatAmount(userData.successAmount)}
                                        </div>
                                    </div>
                                    {hasMultipleTransactions && (
                                        <div className={`expand-icon ${isExpanded ? 'expanded' : ''}`}>
                                            ▼
                                        </div>
                                    )}
                                </div>

                                {/* Show transactions - always show if single transaction, show if expanded for multiple */}
                                {(hasMultipleTransactions ? isExpanded : true) && (
                                    <div className="user-transactions-table">
                                        <table className="transaction-table">
                                            <thead>
                                                <tr>
                                                    <th>Amount</th>
                                                    <th>Purpose</th>
                                                    <th>Status</th>
                                                    <th className="hide-mobile">Payment ID</th>
                                                    <th className="hide-mobile">Date</th>
                                                    <th>Actions</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {userData.transactions.map((tx, index) => (
                                                    <tr key={tx.id || index}>
                                                        <td>
                                                            <span className="amount">{formatAmount(tx.amount)}</span>
                                                        </td>
                                                        <td>
                                                            <span className="purpose">{tx.purpose || 'N/A'}</span>
                                                        </td>
                                                        <td>
                                                            <span className={`status-badge ${getStatusClass(tx.status)}`}>
                                                                {tx.status || 'Unknown'}
                                                            </span>
                                                        </td>
                                                        <td className="hide-mobile">
                                                            <span className="payment-id">{tx.razorpay_payment_id || 'N/A'}</span>
                                                        </td>
                                                        <td className="hide-mobile">
                                                            <span className="date">{formatDate(tx.created_at)}</span>
                                                        </td>
                                                        <td>
                                                            <button
                                                                onClick={() => alert(`Transaction Details:\n\nUser: ${tx.user_name}\nEmail: ${tx.user_email}\nAmount: ${formatAmount(tx.amount)}\nPurpose: ${tx.purpose}\nStatus: ${tx.status}\nPayment ID: ${tx.razorpay_payment_id || 'N/A'}\nOrder ID: ${tx.razorpay_order_id || 'N/A'}\nCreated: ${formatDate(tx.created_at)}\nVerified: ${formatDate(tx.verified_at)}`)}
                                                                className="view-button"
                                                                title="View Details"
                                                            >
                                                                👁️ View
                                                            </button>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export default TransactionList;
